import { AvailabilityColumns } from '@miq/catalog';
import { AvailabilitySpaceClass } from '../../../../../../models/space/availability-space.class';
import { formatDate, generateLastUpdatedByMessage } from '../../../../../../shared/utils/functions';
import { AreaUrl } from '../../details-overlay/details-overlay.constants';
import { DetailsOverlayData } from '../../details-overlay/details-overlay.model';
import { getColumnLabel } from '../details-modal.constants';
import {
  isAvailabilityVacant,
  isDateAvailableValid,
  validateMinMaxAskingRate,
  notEmptySpaceUOM,
  spaceSizeLessThanFloorSize,
} from '../details-modal.validators';
import { areaUomAbbrev, formatNumber } from '@miq/utils';
export const AVAILABILITY_CONFIG: DetailsOverlayData<AvailabilitySpaceClass> = {
  id: space => space.id.toString(),
  title: space => `Suite: ${space.suiteNumber}`,
  navigateToEntity: space => (space.id ? `records/availability/${space.id}/edit` : null),
  validators: [
    {
      groupName: 'formGroupSpace',
      validator: [notEmptySpaceUOM, spaceSizeLessThanFloorSize],
    },
  ],
  formValidator: [isDateAvailableValid, validateMinMaxAskingRate],
  subsets: [
    {
      id: AvailabilityColumns.FloorSuite_FloorNumber,
      dataField: 'floorNumber',
      inputType: 'textbox',
      rawValueFn: space => space.getFloorNumber(),
      displayFn: space => space.getFloorNumber(),
      validationFn: space => space.areFloorAndSuitRequiredValidation(),
      labelFn: columns => getColumnLabel(columns, AvailabilityColumns.FloorSuite_FloorNumber),
    },
    {
      id: AvailabilityColumns.FloorSuite_Suite,
      dataField: 'suiteNumber',
      inputType: 'textbox',
      rawValueFn: space => space.suiteNumber,
      displayFn: space => space.suiteNumber,
      class: 'suite',
      validationFn: space => space.areFloorAndSuitRequiredValidation(),
      labelFn: columns => getColumnLabel(columns, AvailabilityColumns.FloorSuite_Suite),
    },
    {
      id: AvailabilityColumns.SpaceDetails_AvailableSpace,
      dataField: 'spaceSize',
      formGroup: 'formGroupSpace',
      inputType: 'numeric',
      labelFn: columns => getColumnLabel(columns, AvailabilityColumns.SpaceDetails_AvailableSpace),
      unit: {
        dataField: 'spaceSizeUOM',
        formGroup: 'formGroupSpace',
        inputType: 'dropdown',
        lookupCategory: AreaUrl,
        rawValueFn: space => space.getAreaSpaceUOM(),
        displayFn: space => space.getAreaSpaceUOM(),
      },
      rawValueFn: space => space.getAreaSpace(),
      displayFn: (space, column) => {
        const spaceSize = space.getAreaSpace();
        if (spaceSize === null) {
          return null;
        }
        return formatNumber(spaceSize, 'en-US', column?.options?.format ?? '.0-2');
      },
      isAreaField: true,
    },
    {
      id: AvailabilityColumns.SpaceDetails_DateListed,
      dataField: 'onMarketDate',
      inputType: 'date',
      rawValueFn: space => formatDate(space.dateOnMarket),
      displayFn: space => formatDate(space.dateOnMarket),
      labelFn: columns => getColumnLabel(columns, AvailabilityColumns.SpaceDetails_DateListed),
    },
    {
      id: AvailabilityColumns.SpaceDetails_AvailabilityType,
      lookupCategory: '/lookup/ref?query=availability',
      dataField: 'availabilityType',
      inputType: 'dropdown',
      rawValueFn: space => space.availabilityType,
      displayFn: space => space.availabilityType,
      labelFn: columns => getColumnLabel(columns, AvailabilityColumns.SpaceDetails_AvailabilityType),
    },
    {
      id: AvailabilityColumns.SpaceDetails_DateAvailable,
      dataField: 'dateAvailable',
      inputType: 'date',
      rawValueFn: space => formatDate(space.dateAvailable),
      displayFn: space => formatDate(space.dateAvailable),
      labelFn: columns => getColumnLabel(columns, AvailabilityColumns.SpaceDetails_DateAvailable),
    },
    {
      id: AvailabilityColumns.SpaceDetails_LeaseType,
      lookupCategory: '/lookup/ref/country?lookupcategory=MIQ_LEASE_TYPE',
      dataField: 'leaseType',
      rawValueFn: space => space.leaseType,
      displayFn: space => (space.leaseType === 'SubLease' ? 'Sublease' : space.leaseType),
      inputType: 'dropdown',
      isRequired: false,
      labelFn: columns => getColumnLabel(columns, AvailabilityColumns.SpaceDetails_LeaseType),
    },
    {
      id: 379,
      rawValueFn: space => space.generateListingRepAgency(),
      displayFn: space => space.generateListingRepAgency(),
      labelFn: columns =>
        getColumnLabel(columns, AvailabilityColumns.Companies_ListingAgency, 'overlayDetails.listingBroker'),
    },
    {
      id: AvailabilityColumns.SpaceDetails_AvailabilityStatus,
      dataField: 'availabilityStatus',
      rawValueFn: space => space.getSpaceAvailabilityStatus(),
      displayFn: space => space.getSpaceAvailabilityStatus(),
      inputType: 'dropdown',
      lookupCategory: '/lookup/availabilitystatus',
      labelFn: columns => getColumnLabel(columns, AvailabilityColumns.SpaceDetails_AvailabilityStatus),
    },
    {
      id: AvailabilityColumns.SpaceDetails_Vacant,
      dataField: 'vacant',
      inputType: 'dropdown',
      lookupCategory: '/lookup/yn',
      rawValueFn: space => space.vacantF,
      displayFn: space => (space.vacantF === true ? 'Y' : space.vacantF === false ? 'N' : '-'),
      validators: [isAvailabilityVacant],
      labelFn: columns => getColumnLabel(columns, AvailabilityColumns.SpaceDetails_Vacant),
    },
    {
      id: AvailabilityColumns.Terms_Possession,
      dataField: 'possession',
      inputType: 'dropdown',
      lookupCategory: '/lookup/ref/country?lookupcategory=MIQ_POSSESSION',
      rawValueFn: space => space.possession,
      displayFn: space => space.possession,
      labelFn: columns => getColumnLabel(columns, AvailabilityColumns.Terms_Possession),
    },
    {
      id: AvailabilityColumns.Estimated_Rent,
      dataField: 'estimated',
      inputType: 'dropdown',
      lookupCategory: '/lookup/yn',
      rawValueFn: space => space.getEstimatedAskingRent(),
      displayFn: space => {
        const rentValue = space.getEstimatedAskingRent();
        return rentValue === true ? 'Y' : rentValue === false ? 'N' : '-';
      },
      labelFn: columns => getColumnLabel(columns, AvailabilityColumns.Estimated_Rent),
    },
    {
      id: AvailabilityColumns.Terms_MinAskingRateMonthly,
      dataField: 'minAskingRateMo',
      inputType: 'currency',
      formGroup: 'formMinMonthRentGroup',
      rawValueFn: space => space.generateMinMonthlyRent(),
      displayFn: (space, column) => {
        const minMonthlyRent = space.generateMinMonthlyRent();
        if (minMonthlyRent === null) {
          return null;
        }
        return `$ ${formatNumber(minMonthlyRent, 'en-US', column?.options?.format ?? '1.2-2')}`;
      },
      labelFn: columns => getColumnLabel(columns, AvailabilityColumns.Terms_MinAskingRateMonthly),
      unit: {
        dataField: 'minAskingRateMoUom',
        inputType: 'dropdown',
        formGroup: 'formMinMonthRentGroup',
        lookupCategory: '/lookup/ref/country?lookupcategory=currency',
        rawValueFn: space => space.generateMinMonthlyRentUOM(),
        displayFn: space => areaUomAbbrev(space.generateMinMonthlyRentUOM()),
      },
    },
    {
      id: AvailabilityColumns.Terms_MaxAskingRateMonthly,
      dataField: 'maxAskingRateMo',
      inputType: 'currency',
      formGroup: 'formMaxMonthRentGroup',
      rawValueFn: space => space.generateMaxMonthlyRent(),
      displayFn: (space, column) => {
        const maxMonthlyRent = space.generateMaxMonthlyRent();
        if (maxMonthlyRent === null) {
          return null;
        }
        return `$ ${formatNumber(maxMonthlyRent, 'en-US', column?.options?.format ?? '1.2-2')}`;
      },
      labelFn: columns => getColumnLabel(columns, AvailabilityColumns.Terms_MaxAskingRateMonthly),
      unit: {
        dataField: 'maxAskingRateMoUom',
        inputType: 'dropdown',
        formGroup: 'formMaxMonthRentGroup',
        lookupCategory: '/lookup/ref/country?lookupcategory=currency',
        rawValueFn: space => space.generateMaxMonthlyRentUOM(),
        displayFn: space => areaUomAbbrev(space.generateMaxMonthlyRentUOM()),
      },
    },
    {
      id: AvailabilityColumns.Terms_MinAskingRateYearly,
      dataField: 'minAskingRateYr',
      inputType: 'currency',
      formGroup: 'formMinAskingRateYrGroup',
      unit: {
        dataField: 'minAskingRateYrUom',
        inputType: 'dropdown',
        formGroup: 'formMinAskingRateYrGroup',
        lookupCategory: '/lookup/ref/country?lookupcategory=currency',
        rawValueFn: space => space.generateMinYearlyRentUOM(),
        displayFn: space => areaUomAbbrev(space.generateMinYearlyRentUOM()),
      },
      rawValueFn: space => space.generateMinYearlyRent(),
      displayFn: (space, column) => {
        const minYearlyRent = space.generateMinYearlyRent();
        if (minYearlyRent === null) {
          return null;
        }
        return `$ ${formatNumber(minYearlyRent, 'en-US', column?.options?.format ?? '1.2-2')}`;
      },

      labelFn: columns => getColumnLabel(columns, AvailabilityColumns.Terms_MinAskingRateYearly),
    },
    {
      id: AvailabilityColumns.Terms_MaxAskingRateYearly,
      dataField: 'maxAskingRateYr',
      inputType: 'currency',
      formGroup: 'formMaxAskingRateYrGroup',
      unit: {
        dataField: 'maxAskingRateYrUom',
        inputType: 'dropdown',
        formGroup: 'formMaxAskingRateYrGroup',
        lookupCategory: '/lookup/ref/country?lookupcategory=currency',
        rawValueFn: space => space.generateMaxYearlyRentUOM(),
        displayFn: space => areaUomAbbrev(space.generateMaxYearlyRentUOM()),
      },
      rawValueFn: space => space.generateMaxYearlyRent(),
      displayFn: (space, column) => {
        const minYearlyRent = space.generateMaxYearlyRent();
        if (minYearlyRent === null) {
          return null;
        }
        return `$ ${formatNumber(minYearlyRent, 'en-US', column?.options?.format ?? '1.2-2')}`;
      },
      labelFn: columns => getColumnLabel(columns, AvailabilityColumns.Terms_MaxAskingRateYearly),
    },
    {
      id: AvailabilityColumns.SpaceDetails_MinDivisibleSpace,
      dataField: 'minDivisibleSpace',
      inputType: 'numeric',
      formGroup: 'formGroupMinDivisible',
      unit: {
        dataField: 'minDivisibleSpaceUOM',
        inputType: 'dropdown',
        formGroup: 'formGroupMinDivisible',
        lookupCategory: '/lookup/ref/country?lookupcategory=area',
        rawValueFn: space => space.generateMinDivisibleSpaceUOM(),
        displayFn: space => areaUomAbbrev(space.generateMinDivisibleSpaceUOM()),
      },
      rawValueFn: space => space.generateMinDivisibleSpace(),
      displayFn: (space, column) => {
        const minDivisibleSpace = space.generateMinDivisibleSpace();
        if (minDivisibleSpace === null) {
          return null;
        }
        return formatNumber(minDivisibleSpace, 'en-US', column?.options?.format ?? '.0-2');
      },
      labelFn: columns => getColumnLabel(columns, AvailabilityColumns.SpaceDetails_MinDivisibleSpace),
    },
    {
      id: AvailabilityColumns.SpaceDetails_MaxContiguousSpace,
      dataField: 'contiguousArea',
      inputType: 'numeric',
      formGroup: 'formGroupContiguousArea',
      unit: {
        dataField: 'contiguousAreaUOM',
        inputType: 'dropdown',
        formGroup: 'formGroupContiguousArea',
        lookupCategory: '/lookup/ref/country?lookupcategory=area',
        rawValueFn: space => space.generateMaxContigousUOM(),
        displayFn: space => areaUomAbbrev(space.generateMaxContigousUOM()),
      },
      rawValueFn: space => space.generateMaxContiguous(),
      displayFn: (space, column) => {
        const maxContiguous = space.generateMaxContiguous();
        if (maxContiguous === null) {
          return null;
        }
        return formatNumber(maxContiguous, 'en-US', column?.options?.format ?? '.0-2');
      },
      labelFn: columns => getColumnLabel(columns, AvailabilityColumns.SpaceDetails_MaxContiguousSpace),
    },
  ],
  footer: {
    label: 'overlayDetails.updateBy',
    class: 'last-update',
    displayFn: space => generateLastUpdatedByMessage(space.edpLastUpdateDate, space.edpLastUpdateBy),
  },
};
